// PVT!M366:M395 then M401:M430 — the two refinement stages, 30 points at
// 0.01 then 30 more at 0.001, each restarting one previous step below the
// best point found so far.
const GAS_ROOT_SEARCH_STEP = 0.01;

const GAS_ROOT_SEARCH_STEPS = 30;

const GAS_ROOT_REFINEMENT_STAGES = 2;

// PVT!C364:C404 — the coarse Z grid (0.05 to 2.05 at 0.05) that brackets a
// root before those refinement stages run. The workbook takes the liquid root
// from the low end of that grid (PVT!B364:B370 via G363) and the gas root from
// the remainder (PVT!B371:B404 via L363), so both are slices of one scan.
// Without this stage a gas root outside the first refinement window is never
// found and the search returns its boundary instead.
const COARSE_SEARCH_STEP = 0.05;

const LIQUID_ROOT_SEARCH_START = 0.05;

const LIQUID_ROOT_SEARCH_STEPS = 7;

const GAS_ROOT_SEARCH_START = 0.4;

const GAS_ROOT_SEARCH_COARSE_STEPS = 34;

type RootSolveResult = {
  z: number;
  residual: number;
  // True when `z` landed on the edge of the search interval (PVT!M366:M430
  // for gas, PVT!B364:B370 for liquid — the same interval the reference
  // workbook itself searches), which combined with a large residual means
  // the physical root likely lies outside this port's search range.
  atSearchBoundary: boolean;
};

// Coefficients of the Peng-Robinson cubic
//   Z^3 - (1 - B)Z^2 + (A - 3B^2 - 2B)Z - (AB - B^2 - B^3)
// computed once per root search rather than at every grid point. The
// residual below evaluates in the same order as the inline form did, so
// results are bit-identical.
type CubicCoefficients = { c2: number; c1: number; c0: number };

function getCubicCoefficients(a: number, b: number): CubicCoefficients {
  return {
    c2: 1 - b,
    c1: a - 3 * b ** 2 - 2 * b,
    c0: a * b - b ** 2 - b ** 3
  };
}

function pengRobinsonCubicResidual(
  z: number,
  coefficients: CubicCoefficients
): number {
  return Math.abs(
    z ** 3 - coefficients.c2 * z ** 2 + coefficients.c1 * z - coefficients.c0
  );
}

function refineRoot(
  coefficients: CubicCoefficients,
  initialStart: number,
  initialStep: number
): RootSolveResult {
  let start = initialStart;
  let step = initialStep;
  let best = start;
  let bestResidual = Infinity;

  for (let stage = 0; stage < GAS_ROOT_REFINEMENT_STAGES; stage++) {
    let lowestResidual = Infinity;
    for (let i = 0; i < GAS_ROOT_SEARCH_STEPS; i++) {
      const z = start + i * step;
      const residual = pengRobinsonCubicResidual(z, coefficients);
      if (residual < lowestResidual) {
        lowestResidual = residual;
        best = z;
      }
    }
    bestResidual = lowestResidual;
    start = best - step;
    step = step / 10;
  }

  const atSearchBoundary =
    best <= initialStart ||
    best >= initialStart + initialStep * (GAS_ROOT_SEARCH_STEPS - 1);

  return { z: best, residual: bestResidual, atSearchBoundary };
}

// PVT!B364:B404 — lowest-residual point of one slice of the coarse Z grid,
// used to bracket the root before refineRoot narrows it.
function solveRoot(
  a: number,
  b: number,
  coarseStart: number,
  coarseSteps: number
): RootSolveResult {
  const coefficients = getCubicCoefficients(a, b);
  let coarseBest = coarseStart;
  let lowestCoarseResidual = Infinity;

  for (let i = 0; i < coarseSteps; i++) {
    const z = coarseStart + i * COARSE_SEARCH_STEP;
    const residual = pengRobinsonCubicResidual(z, coefficients);
    if (residual < lowestCoarseResidual) {
      lowestCoarseResidual = residual;
      coarseBest = z;
    }
  }

  return refineRoot(
    coefficients,
    coarseBest - COARSE_SEARCH_STEP,
    GAS_ROOT_SEARCH_STEP
  );
}

// PVT!L363/M433 — gas root, bracketed from the upper part of the coarse grid.
export function solveGasRoot(a: number, b: number): RootSolveResult {
  return solveRoot(a, b, GAS_ROOT_SEARCH_START, GAS_ROOT_SEARCH_COARSE_STEPS);
}

// PVT!G363/H433 — liquid root, bracketed from the low end of the coarse grid.
export function solveLiquidRoot(a: number, b: number): RootSolveResult {
  return solveRoot(a, b, LIQUID_ROOT_SEARCH_START, LIQUID_ROOT_SEARCH_STEPS);
}
